import { forwardRef, useEffect, useRef } from 'react';

import { SegmentedButtons } from '@gds/SegmentedButtons/SegmentedButtons';

import styles from './ContentsLinks.module.css';

interface ContentsLinksProps {
  activeSectionId: string | undefined;
  hasCommonQuestions?: boolean;
  strings: Record<string, string>;
}

const ContentsLinks = forwardRef<HTMLElement, ContentsLinksProps>(
  ({ activeSectionId, hasCommonQuestions, strings }: ContentsLinksProps, ref) => {
    const segmentedButtonsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!segmentedButtonsRef.current || !activeSectionId) return;
      const activeBtn =
        segmentedButtonsRef.current.querySelector<HTMLElement>('[aria-checked="true"]');
      activeBtn?.scrollIntoView({ inline: 'nearest', behavior: 'smooth' });
    }, [activeSectionId]);

    const handleSectionChange = (sectionId: string) => {
      const section = document.getElementById(sectionId);
      if (!section) return;
      const scrollOffset = window.matchMedia('(min-width: 1024px)').matches ? 168 : 34;

      window.scrollTo({
        top: window.scrollY + section.getBoundingClientRect().top - scrollOffset,
        behavior: 'smooth',
      });
    };

    const buttons = [
      { label: strings.overall, value: 'overall' },
      { label: strings.inside, value: 'inside' },
      { label: strings.driving, value: 'driving' },
      { label: strings.runningCost, value: 'running-cost' },
      { label: strings.versions, value: 'versions' },
      ...(hasCommonQuestions ? [{ label: strings.commonQuestions, value: 'questions' }] : []),
    ];

    return (
      <section className={styles.wrapper} ref={ref}>
        <div ref={segmentedButtonsRef}>
          <SegmentedButtons
            currentValue={activeSectionId}
            buttons={buttons}
            onChange={handleSectionChange}
          />
        </div>
      </section>
    );
  },
);

ContentsLinks.displayName = 'ContentsLinks';

export default ContentsLinks;
